import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { lcCoachingApi } from '@/api/lcCoaching'
import type { DesignatedReviewView } from '@/types/coaching'
import { coachingKeys } from './useCoachingToday'

interface PlacementInput {
  anchorLessonId: number
  position: 'BEFORE'
}

interface MutationContext {
  previous: DesignatedReviewView[] | undefined
}

export function useDesignatedReviews(learnerId: number) {
  return useQuery({
    queryKey: coachingKeys.designatedReviews(learnerId),
    queryFn: () => lcCoachingApi.listDesignatedReviews(learnerId),
    enabled: Number.isFinite(learnerId) && learnerId > 0,
    staleTime: 30_000,
  })
}

export function useDesignatedReviewMutationPending(learnerId: number, lessonId: number): boolean {
  return useIsMutating({
    mutationKey: coachingKeys.designatedReviewMutation(learnerId, lessonId),
    exact: true,
  }) > 0
}

async function invalidateReviewQueries(
  learnerId: number,
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: coachingKeys.designatedReviews(learnerId) }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.learnerToday(learnerId) }),
    queryClient.invalidateQueries({ queryKey: ['coaching', 'learner', learnerId, 'library'] }),
    queryClient.invalidateQueries({ queryKey: coachingKeys.today() }),
  ])
}

function upsertReservation(
  rows: DesignatedReviewView[] | undefined,
  next: DesignatedReviewView,
): DesignatedReviewView[] {
  if (!rows) return [next]
  const index = rows.findIndex((row) => row.reviewLessonId === next.reviewLessonId)
  if (index === -1) return [...rows, next]
  return rows.map((row, rowIndex) => rowIndex === index ? next : row)
}

export function useDesignateReview(learnerId: number, lessonId: number) {
  const queryClient = useQueryClient()
  const queryKey = coachingKeys.designatedReviews(learnerId)

  return useMutation<DesignatedReviewView, unknown, PlacementInput, MutationContext>({
    mutationKey: coachingKeys.designatedReviewMutation(learnerId, lessonId),
    mutationFn: (placement) => lcCoachingApi.designateReview(learnerId, {
      lessonId,
      ...placement,
    }),
    onMutate: async (placement) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<DesignatedReviewView[]>(queryKey)
      queryClient.setQueryData<DesignatedReviewView[]>(queryKey, (rows) => rows?.map((row) =>
        row.reviewLessonId === lessonId
          ? {
              ...row,
              anchorLessonId: placement.anchorLessonId,
              anchorLessonTitle: null,
              position: placement.position,
              placementStatus: 'SCHEDULED',
              result: null,
              warningCode: null,
            }
          : row,
      ))
      return { previous }
    },
    onSuccess: async (reservation) => {
      queryClient.setQueryData<DesignatedReviewView[]>(queryKey, (rows) =>
        upsertReservation(rows, reservation))
      await invalidateReviewQueries(learnerId, queryClient)
      queryClient.setQueryData<DesignatedReviewView[]>(queryKey, (rows) =>
        upsertReservation(rows, reservation))
    },
    onError: async (_error, _placement, context) => {
      queryClient.setQueryData(queryKey, context?.previous)
      await invalidateReviewQueries(learnerId, queryClient)
    },
  })
}

export function useCancelReview(learnerId: number, lessonId: number) {
  const queryClient = useQueryClient()
  const queryKey = coachingKeys.designatedReviews(learnerId)

  return useMutation<{ reviewLessonId: number; cancelled: boolean }, unknown, void, MutationContext>({
    mutationKey: coachingKeys.designatedReviewMutation(learnerId, lessonId),
    mutationFn: () => lcCoachingApi.cancelDesignatedReview(learnerId, lessonId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<DesignatedReviewView[]>(queryKey)
      queryClient.setQueryData<DesignatedReviewView[]>(queryKey, (rows) =>
        rows?.filter((row) => row.reviewLessonId !== lessonId))
      return { previous }
    },
    onSuccess: async ({ reviewLessonId }) => {
      queryClient.setQueryData<DesignatedReviewView[]>(queryKey, (rows) =>
        rows?.filter((row) => row.reviewLessonId !== reviewLessonId))
      await invalidateReviewQueries(learnerId, queryClient)
      queryClient.setQueryData<DesignatedReviewView[]>(queryKey, (rows) =>
        rows?.filter((row) => row.reviewLessonId !== reviewLessonId))
    },
    onError: async (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.previous)
      await invalidateReviewQueries(learnerId, queryClient)
    },
  })
}
